import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import BackButton from '../components/BackButton';

function formatCentimeters(valueMm) {
  if (typeof valueMm !== 'number') {
    return null;
  }

  return (valueMm / 10).toFixed(1).replace('.', ',');
}

function buildVariantFormatSummary(variant) {
  const widthCm = formatCentimeters(variant?.target_width_mm);
  const heightCm = formatCentimeters(variant?.target_height_mm);

  if (widthCm && heightCm) {
    return `${variant?.target_format_label} - ${widthCm} x ${heightCm} cm • ${variant?.target_width_px} x ${variant?.target_height_px}px`;
  }

  return `${variant?.target_format_label} • ${variant?.target_width_px} x ${variant?.target_height_px}px`;
}

function VariantPagesPage() {
  const { variantId } = useParams();
  const navigate = useNavigate();
  const [variant, setVariant] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [regeneratingPageId, setRegeneratingPageId] = useState('');
  const [selectedImagePage, setSelectedImagePage] = useState(null);

  const buttonClassName =
    'inline-flex rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60';

  useEffect(() => {
    const load = async () => {
      try {
        const [variantRes, pagesRes] = await Promise.all([
          axios.get(`/api/variants/${variantId}`),
          axios.get(`/api/variants/${variantId}/pages`),
        ]);
        setVariant(variantRes.data);
        setPages(pagesRes.data.pages || []);
      } catch (err) {
        setError('Failed to load variant pages.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [variantId]);

  if (loading) {
    return <div className="max-w-6xl mx-auto mt-16">Loading variant pages...</div>;
  }

  const handleExport = async () => {
    setExporting(true);
    setError('');

    try {
      const response = await axios.get(`/api/variants/${variantId}/export-pdf`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${variant?.name || 'book-variant'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export variant PDF.');
    } finally {
      setExporting(false);
    }
  };

  const updatePage = (pageId, updatedPage) => {
    setPages((prevPages) =>
      prevPages.map((page) => (page._id === pageId ? updatedPage : page)),
    );
    setSelectedImagePage((prev) => (prev?._id === pageId ? updatedPage : prev));
  };

  const handleRegenerate = async (pageId, updates) => {
    setRegeneratingPageId(pageId);
    setError('');

    try {
      const response = await axios.post(
        `/api/variant-pages/${pageId}/regenerate`,
        updates,
      );

      const updatedPage = response.data.page;
      updatePage(pageId, updatedPage);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to regenerate variant page.');
    } finally {
      setRegeneratingPageId('');
    }
  };

  const handleModeReset = async (pageId, resizeMode) => {
    await handleRegenerate(pageId, {
      resize_mode: resizeMode,
      scale_percent: 100,
      offset_x: 0,
      offset_y: 0,
    });
  };

  const handleScaleAdjust = async (page, deltaPercent) => {
    const nextScale = Math.max(10, (page.resize_scale_percent || 100) + deltaPercent);
    await handleRegenerate(page._id, {
      resize_mode: page.resize_mode || 'fit',
      scale_percent: nextScale,
      offset_x: page.offset_x || 0,
      offset_y: page.offset_y || 0,
    });
  };

  const handleOffsetAdjust = async (page, axis, delta) => {
    const nextOffsetX = axis === 'x' ? (page.offset_x || 0) + delta : (page.offset_x || 0);
    const nextOffsetY = axis === 'y' ? (page.offset_y || 0) + delta : (page.offset_y || 0);
    await handleRegenerate(page._id, {
      resize_mode: page.resize_mode || 'fit',
      scale_percent: page.resize_scale_percent || 100,
      offset_x: nextOffsetX,
      offset_y: nextOffsetY,
    });
  };

  const handlePromoteToBook = async () => {
    const confirmed = window.confirm(
      'Create a new normal book from this variant and continue editing it there?',
    );
    if (!confirmed) {
      return;
    }

    setPromoting(true);
    setError('');

    try {
      const response = await axios.post(`/api/variants/${variantId}/promote-to-book`);
      navigate(`/books/${response.data.book_id}/edit`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to promote variant to book.');
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto mt-10 p-6">
      <BackButton className="absolute" />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{variant?.name}</h1>
            <p className="mt-2 text-gray-600">
              {buildVariantFormatSummary(variant)}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handlePromoteToBook}
              disabled={promoting}
              className={buttonClassName}
            >
              {promoting ? 'Promoting...' : 'Promote To Book'}
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className={buttonClassName}
            >
              {exporting ? 'Exporting PDF...' : 'Export Variant PDF'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {pages.map((page) => (
          <div
            key={page._id}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <button
              type="button"
              onClick={() => setSelectedImagePage(page)}
              className="block w-full bg-gray-50"
            >
              <img
                src={`/uploads/${page.image_path}`}
                alt={page.name}
                className="h-72 w-full object-contain"
              />
            </button>
            <div className="p-4">
              <div className="font-medium text-gray-900">{page.name}</div>
              <div className="text-sm text-gray-500 mb-3">
                Page {page.page_number + 1}
              </div>
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Mode: {page.resize_mode || variant?.resize_mode || 'fit'}
              </div>
              <div className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500">
                Scale: {page.resize_scale_percent || 100}% • Offset: {page.offset_x || 0},{' '}
                {page.offset_y || 0}
              </div>
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleModeReset(page._id, 'fit')}
                  disabled={regeneratingPageId === page._id}
                  className={buttonClassName}
                >
                  {regeneratingPageId === page._id
                    ? 'Updating...'
                    : 'Run Fit'}
                </button>
                <button
                  type="button"
                  onClick={() => handleModeReset(page._id, 'cover')}
                  disabled={regeneratingPageId === page._id}
                  className={buttonClassName}
                >
                  {regeneratingPageId === page._id
                    ? 'Updating...'
                    : 'Run Cover'}
                </button>
                <button
                  type="button"
                  onClick={() => handleScaleAdjust(page, -10)}
                  disabled={regeneratingPageId === page._id}
                  className={buttonClassName}
                >
                  Scale -10%
                </button>
                <button
                  type="button"
                  onClick={() => handleScaleAdjust(page, 10)}
                  disabled={regeneratingPageId === page._id}
                  className={buttonClassName}
                >
                  Scale +10%
                </button>
                </div>
                <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleOffsetAdjust(page, 'x', -40)}
                  disabled={regeneratingPageId === page._id}
                  className={buttonClassName}
                >
                  Left
                </button>
                <button
                  type="button"
                  onClick={() => handleOffsetAdjust(page, 'x', 40)}
                  disabled={regeneratingPageId === page._id}
                  className={buttonClassName}
                >
                  Right
                </button>
                <button
                  type="button"
                  onClick={() => handleOffsetAdjust(page, 'y', -40)}
                  disabled={regeneratingPageId === page._id}
                  className={buttonClassName}
                >
                  Up
                </button>
                <button
                  type="button"
                  onClick={() => handleOffsetAdjust(page, 'y', 40)}
                  disabled={regeneratingPageId === page._id}
                  className={buttonClassName}
                >
                  Down
                </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedImagePage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
          onClick={() => setSelectedImagePage(null)}
        >
          <div
            className="max-h-full max-w-6xl rounded-xl bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between gap-4">
              <div>
                <div className="font-medium text-gray-900">
                  {selectedImagePage.name}
                </div>
                <div className="text-sm text-gray-500">
                  Page {selectedImagePage.page_number + 1}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedImagePage(null)}
                className={buttonClassName}
              >
                Close
              </button>
            </div>
            <img
              src={`/uploads/${selectedImagePage.image_path}`}
              alt={selectedImagePage.name}
              className="max-h-[80vh] max-w-full object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default VariantPagesPage;
